import { JsonLd } from "../components/JsonLd";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, ChevronRight, ChevronDown, Search, Globe2,
  HelpCircle, Compass, Clock, AlertTriangle, CheckCircle2,
  ExternalLink, Shield, XCircle, Calendar, FileText,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";

const STEPS = [
  {
    title: "Go to the Official Entrant Status Check",
    content: "Navigate to dvprogram.state.gov — this is the ONLY official website for checking DV Lottery results. Do not use any other website, as there are many scam sites that impersonate the State Department. The official site is free. You will never be asked to pay to check your results.",
    icon: Globe2,
    color: "bg-teal-700 text-white",
  },
  {
    title: "Click \"Check Status\"",
    content: "On the homepage, look for the \"Entrant Status Check\" link. It will be clearly labeled. Click it to proceed to the verification form. The link is typically available from early May through at least September 30 of the same year.",
    icon: Search,
    color: "bg-sky-600 text-white",
  },
  {
    title: "Enter Your Confirmation Number",
    content: "You will need the confirmation number you received when you originally submitted your DV Lottery entry. This is a 16-character alphanumeric code. If you lost your confirmation number, there is no way to recover it — the State Department does not provide replacement confirmation numbers. This is why it is critical to save your confirmation number immediately after submitting your entry.",
    icon: FileText,
    color: "bg-violet-600 text-white",
  },
  {
    title: "Enter Your Personal Information",
    content: "You will also need to enter your last/family name, and your year of birth exactly as you entered them on your original application. These must match precisely — even small differences (such as a middle name included or excluded) can prevent you from accessing your results.",
    icon: Shield,
    color: "bg-amber-600 text-white",
  },
  {
    title: "Submit and View Your Results",
    content: "After entering all information, click \"Submit.\" The system will display one of two results: \"You have been selected\" (congratulations — you have been randomly chosen for further processing) or \"You have not been selected\" (you were not chosen in this year's lottery, but you can apply again next year). If selected, the page will also display further instructions for scheduling your immigrant visa interview.",
    icon: CheckCircle2,
    color: "bg-emerald-600 text-white",
  },
];

const MISTAKES = [
  {
    title: "Using unofficial or scam websites",
    desc: "The ONLY official site is dvprogram.state.gov. Scammers create convincing lookalike sites that charge fees or steal personal data. The U.S. government never charges a fee to check DV Lottery results.",
  },
  {
    title: "Losing your confirmation number",
    desc: "Without your confirmation number, you cannot check your results. The State Department does not reissue confirmation numbers under any circumstances. Save it in multiple secure locations immediately after applying.",
  },
  {
    title: "Checking too early or too late",
    desc: "Results are typically available from early May. Checking before this date will show no results. The Entrant Status Check remains available through at least September 30, but waiting too long could mean missing critical deadlines if you are selected.",
  },
  {
    title: "Entering incorrect personal information",
    desc: "Your name and year of birth must match your original application exactly. Even minor discrepancies — like using a nickname instead of your legal name — will prevent the system from finding your entry.",
  },
  {
    title: "Falling for fake selection notifications",
    desc: "The U.S. government does NOT notify winners by email, phone, or social media. If you receive an email saying you won the DV Lottery, it is a scam. The only way to verify your selection is through the official Entrant Status Check.",
  },
];

const IF_SELECTED = [
  "Your status page will say \"You have been selected\" and provide a case number",
  "You will receive instructions to complete Form DS-260 (Immigrant Visa Application) online",
  "Begin gathering required documents immediately: passport, birth certificate, police clearances, medical exam, financial evidence",
  "Schedule and attend your immigrant visa interview at the U.S. Embassy or Consulate in your country",
  "All processing must be completed before September 30 of the fiscal year — there are no extensions",
  "Being selected does NOT guarantee a visa. You must still qualify and complete all steps before the deadline",
];

const IF_NOT_SELECTED = [
  "Your status page will say \"You have not been selected.\" This is final for the current year",
  "There is no appeal process for non-selection",
  "You are eligible to apply again in the next DV Lottery registration period (usually October-November)",
  "Each year's lottery is independent — previous non-selection does not affect future chances",
  "Consider exploring other immigration pathways through our Green Card Guide",
];

const DEADLINES = [
  { event: "DV Lottery Registration Opens", time: "Early October", note: "Submit your entry at dvprogram.state.gov" },
  { event: "Registration Closes", time: "Early November", note: "Exact date varies each year — do not wait until the last day" },
  { event: "Results Available", time: "Early May (following year)", note: "Check via Entrant Status Check on official site" },
  { event: "DS-260 Submission Deadline", time: "As soon as possible after selection", note: "Submit early to secure an interview appointment" },
  { event: "Visa Interview", time: "Varies by case number and embassy", note: "Scheduled by the Kentucky Consular Center (KCC)" },
  { event: "Fiscal Year Deadline", time: "September 30", note: "All visas must be issued before this date — no extensions" },
];

const FAQS = [
  {
    q: "When are DV Lottery results released?",
    a: "Results for the DV Lottery are typically released in early May, approximately 6 months after the registration period closes. The exact date is announced on the official State Department website. For DV-2026, results are expected in May 2025. You can check results at dvprogram.state.gov using your confirmation number.",
  },
  {
    q: "Can I check my DV Lottery results by email or phone?",
    a: "No. The U.S. government does NOT notify winners by email, phone, letter, or social media. The only official way to check your DV Lottery results is through the Entrant Status Check at dvprogram.state.gov. Any communication claiming you won via email or phone is a scam.",
  },
  {
    q: "What if I lost my confirmation number?",
    a: "Unfortunately, if you lose your confirmation number, there is no way to check your results. The State Department does not provide replacement confirmation numbers. To avoid this in the future, save your confirmation number in multiple secure locations — email it to yourself, take a screenshot, and write it down.",
  },
  {
    q: "Does being selected in the DV Lottery guarantee a Green Card?",
    a: "No. Being selected means you are eligible to apply for a Diversity Visa, but you must still complete the full application process: submit Form DS-260, gather all required documents, pass a background check, attend a visa interview, and demonstrate you are admissible to the United States. Additionally, more people are selected than there are visas available, so you must complete the process before visas run out.",
  },
  {
    q: "Can I check results for a previous year's lottery?",
    a: "The Entrant Status Check typically remains available for at least one year after results are released. However, if the fiscal year for your lottery has passed (September 30), and you did not complete processing, your selection is no longer valid regardless of what the status check shows.",
  },
  {
    q: "How many people are selected in the DV Lottery each year?",
    a: "Approximately 100,000-120,000 people are selected each year, even though only 55,000 Diversity Visas are available. More people are selected than there are visas because not all selectees will complete the visa process. Selection is done by random computer drawing from all qualified entries.",
  },
  {
    q: "Is the DV Lottery results check free?",
    a: "Yes, checking your DV Lottery results is completely free. You should never pay anyone to check your results. The official Entrant Status Check at dvprogram.state.gov does not charge any fees. If a website asks you to pay to check results, it is a scam.",
  },
];

export default function DVCheckResultsPage() {
  const lp = useLocalePath();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-background" data-testid="dv-check-results-page">
      <SEOHead
        title="How to Check DV Lottery Results 2026 (Official Steps)"
        description="Step-by-step guide to checking your DV Lottery results at dvprogram.state.gov for 2026. What to do if selected, common mistakes to avoid, and critical deadlines."
      />

      {/* JSON-LD Article Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Check DV Lottery Results 2026 (Official Steps)",
            description: "Step-by-step guide to checking your DV Lottery results at dvprogram.state.gov for 2026.",
            dateModified: "2026-02-01",
            url: "https://immigrationguide.us/how-to-check-dv-lottery-results",
            publisher: { "@type": "Organization", name: "ImmigrationGuide", url: "https://immigrationguide.us" },
            mainEntityOfPage: { "@type": "WebPage", "@id": "https://immigrationguide.us/how-to-check-dv-lottery-results" },
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvcr-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 mb-5 font-medium">
            <Search className="h-3.5 w-3.5 mr-1.5" />
            DV Lottery Results Guide
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            How to Check Your DV Lottery Results (2026 Update)
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            The only official way to check your Diversity Visa Lottery results is through the Entrant Status
            Check at dvprogram.state.gov — it is free and takes less than 2 minutes. Below are the exact steps,
            common mistakes that lock people out, and what to do next whether you are selected or not.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://dvprogram.state.gov" target="_blank" rel="noopener noreferrer" data-testid="dvcr-official-link">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base">
                <ExternalLink className="mr-2 h-4 w-4" /> Go to Official Site
              </Button>
            </a>
            <Link to={lp("/gc")} data-testid="dvcr-hero-gc">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <Compass className="mr-2 h-4 w-4" /> Full Green Card Guide
              </Button>
            </Link>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
            <Shield className="h-3.5 w-3.5 text-teal-600" />
            <span>Last updated: February 2026 · Based on official U.S. Department of State data</span>
          </div>
        </div>
      </section>

      {/* STEP-BY-STEP */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvcr-steps">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">Step-by-Step</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              How to Check Your Results
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <Card key={i} className="border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${step.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 mb-2">
                          Step {i + 1}: {step.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-slate-600">{step.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="dvcr-mid-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <p className="text-sm text-slate-600">
            Already checked your results?{" "}
            <a href="https://immigrationguide.us/auth?mode=register" target="_blank" rel="noopener noreferrer" className="text-teal-700 font-medium hover:text-teal-800 underline underline-offset-2">
              Track your next steps automatically
            </a>{" "}
            with our free immigration guide.
          </p>
        </div>
      </section>

      {/* COMMON MISTAKES */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dvcr-mistakes">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-rose-600 mb-2">Avoid These Errors</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              Common Mistakes When Checking Results
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {MISTAKES.map((m, i) => (
              <Card key={i} className="border-rose-100 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{m.title}</h3>
                      <p className="text-sm leading-relaxed text-slate-600">{m.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* IF SELECTED / NOT SELECTED */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvcr-outcomes">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              What to Do After Checking Your Results
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-emerald-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold text-emerald-800">If You Are Selected</h3>
                </div>
                <ul className="space-y-3">
                  {IF_SELECTED.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-600">
                      <span className="text-emerald-600 font-bold flex-shrink-0">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-emerald-100">
                  <Link to={lp("/what-to-do-after-winning-dv-lottery")} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800">
                    Read full post-selection guide <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-slate-500" />
                  <h3 className="text-lg font-semibold text-slate-800">If You Are Not Selected</h3>
                </div>
                <ul className="space-y-3">
                  {IF_NOT_SELECTED.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-600">
                      <span className="text-slate-400 font-bold flex-shrink-0">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <Link to={lp("/gc/green-card-guide")} className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800">
                    Explore other Green Card pathways <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* DEADLINES TABLE */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dvcr-deadlines">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-amber-600 mb-2">Critical Dates</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              DV Lottery Key Deadlines
            </h2>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 max-w-3xl mx-auto">
            <table className="w-full text-sm" data-testid="dvcr-deadlines-table">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Event</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">When</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden md:table-cell">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {DEADLINES.map((d, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{d.event}</td>
                    <td className="px-4 py-3 text-teal-700 font-medium whitespace-nowrap">{d.time}</td>
                    <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{d.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-10 bg-white border-t border-slate-100" data-testid="dvcr-internal-links">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Related Green Card Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to={lp("/gc")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card Hub</h3>
              <p className="text-xs text-slate-500">Complete guide to all Green Card pathways</p>
            </Link>
            <Link to={lp("/gc/green-card-guide")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card Categories</h3>
              <p className="text-xs text-slate-500">Family, employment, lottery, and more</p>
            </Link>
            <Link to={lp("/gc/green-card-timeline")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Processing Timeline</h3>
              <p className="text-xs text-slate-500">How long each category takes step by step</p>
            </Link>
            <Link to={lp("/gc/documents-checklist")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Documents Checklist</h3>
              <p className="text-xs text-slate-500">32 required documents organized by category</p>
            </Link>
            <Link to={lp("/gc/faq")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card FAQ</h3>
              <p className="text-xs text-slate-500">19 common questions answered clearly</p>
            </Link>
            <Link to={lp("/dv-lottery-eligibility")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">DV Lottery Eligibility</h3>
              <p className="text-xs text-slate-500">Check if you qualify for the Diversity Visa</p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dvcr-faq">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">Common Questions</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              DV Lottery Results FAQ
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-200 shadow-sm overflow-hidden">
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left cursor-pointer"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvcr-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvcr-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">
            Start Your Green Card Journey
          </h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">
            Whether you were selected in the DV Lottery or exploring other pathways, our AI-powered guide walks you through every step.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/gc")} data-testid="dvcr-final-gc">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                Green Card Guide <ArrowRight className="mr-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvcr-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                Start Free Guide <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/how-to-check-dv-lottery-results" />
      <SiteFooter />
    </div>
  );
}
